// Iter-419 — Head metadata singleton enforcement.
//
// Bing reported the same 78 URLs under three errors: "multiple titles",
// "multiple descriptions" and "more than one canonical tag".
//
// ROOT CAUSE (reproduced): CarSavr is a CRA SPA whose metadata is owned
// by react-helmet-async, and React 19 hoists those <title>/<meta>/<link>
// elements by APPENDING them to <head>. The previous implementation
// reconciled duplicates by removing the twins that carried the
// `data-seo-static` marker (the tags shipped in index.html + the ones
// the pre-hydration script creates). That works for the plain CRA
// response — but crawlers are served a PRE-RENDERED SNAPSHOT of the
// hydrated DOM (`x-rendered-by: crawler-cache`), and in that snapshot
// the surviving tags are React's own, i.e. NOT marked `data-seo-static`.
// When a rendering bot boots the SPA from that snapshot, Helmet appends
// a second title/description/canonical and the marker-based logic has
// nothing to match, so the duplicates stay — exactly what Bing saw.
//
// FIX: stop reasoning about markers and enforce cardinality instead.
// Group every metadata element in <head> by its identity key and keep
// exactly ONE per key, whatever produced it. This is idempotent for any
// serving path (plain CRA, /api/ssr/render output, or a re-hydrated
// crawler snapshot) and it cannot regress as new metadata systems or
// programmatic templates are added.

const STATIC_ATTR = "data-seo-static";

// Identity key for the singleton groups we own. Anything that returns
// null is left alone (JSON-LD blocks, preloads, icons, viewport, …).
function metaKey(el) {
  const tag = el.tagName;
  if (tag === "TITLE") return "title";
  if (tag === "META") {
    const name = el.getAttribute("name");
    if (name === "description" || name === "robots") return `meta:name:${name}`;
    if (name && name.startsWith("twitter:")) return `meta:name:${name}`;
    const prop = el.getAttribute("property");
    if (prop && prop.startsWith("og:")) return `meta:prop:${prop}`;
    return null;
  }
  if (tag === "LINK") {
    const rel = (el.getAttribute("rel") || "").toLowerCase();
    if (rel === "canonical") return "link:canonical";
    if (rel === "alternate" && el.getAttribute("hreflang")) {
      return `link:alternate:${el.getAttribute("hreflang")}`;
    }
    return null;
  }
  return null;
}

// Which duplicate survives: prefer a React/Helmet-owned element (no
// `data-seo-static` marker) because it reflects the route the SPA is
// actually rendering; among equals keep the last one written, which is
// the freshest value after a client-side navigation.
function pickSurvivor(els) {
  const reactOwned = els.filter((el) => !el.hasAttribute(STATIC_ATTR));
  const pool = reactOwned.length ? reactOwned : els;
  return pool[pool.length - 1];
}

// Iter-422 — JSON-LD singleton enforcement (Bing audit §4.6).
//
// The head accumulated the entity graph from THREE producers: the static
// `@graph` in index.html, the SSR `data-ssr` injection, and react-helmet.
// The upstream producers were fixed (SEO.jsx no longer emits the global
// entities; the SSR injector skips types the template already declares),
// but a re-hydrated crawler snapshot can still stack page-scoped blocks,
// so cardinality is enforced here too — same philosophy as the meta tags
// above: reason about counts, not about who produced what.
//
// ONLY types that are legitimately one-per-page are touched. Product,
// Review, ItemList, DefinedTerm, Dataset, SoftwareApplication etc. can
// repeat on a single page (e.g. 5 lender rate cards) and are never
// filtered.
const LD_SINGLETON_TYPES = new Set([
  "Organization",
  "WebSite",
  "WebPage",
  "BreadcrumbList",
  "FAQPage",
  "Article",
  "NewsArticle",
  "BlogPosting",
]);

function ldTypesOf(el) {
  let data;
  try {
    data = JSON.parse(el.textContent || "null");
  } catch {
    return null;
  }
  if (!data || typeof data !== "object") return null;
  const entries = Array.isArray(data) ? data : [data];
  const flat = [];
  for (const entry of entries) {
    if (entry && typeof entry === "object" && Array.isArray(entry["@graph"])) {
      flat.push(...entry["@graph"]);
    } else {
      flat.push(entry);
    }
  }
  const types = flat
    .filter((e) => e && typeof e === "object" && typeof e["@type"] === "string")
    .map((e) => e["@type"]);
  if (!types.length) return null;
  return { types, isGraph: flat.length > 1 };
}

function dedupeJsonLd(head) {
  const els = Array.from(
    head.querySelectorAll('script[type="application/ld+json"]'),
  );
  if (els.length < 2) return 0;

  const parsed = new Map();
  const claimedByGraph = new Set();
  for (const el of els) {
    const info = ldTypesOf(el);
    if (!info) continue;
    parsed.set(el, info);
    if (info.isGraph) {
      for (const t of info.types) {
        if (LD_SINGLETON_TYPES.has(t)) claimedByGraph.add(t);
      }
    }
  }

  let removed = 0;
  // 1. A standalone singleton block whose type is already inside a
  //    multi-entity @graph is redundant — the graph version carries the
  //    `@id` that ties the entity together, so it always wins.
  for (const [el, info] of Array.from(parsed.entries())) {
    if (info.isGraph) continue;
    if (info.types.length !== 1) continue;
    if (!claimedByGraph.has(info.types[0])) continue;
    el.remove();
    parsed.delete(el);
    removed += 1;
  }

  // 2. Collapse remaining same-type singleton blocks to one.
  const byType = new Map();
  for (const [el, info] of parsed.entries()) {
    if (info.isGraph) continue;
    if (info.types.length !== 1) continue;
    const t = info.types[0];
    if (!LD_SINGLETON_TYPES.has(t)) continue;
    const bucket = byType.get(t);
    if (bucket) bucket.push(el);
    else byType.set(t, [el]);
  }
  for (const list of byType.values()) {
    if (list.length < 2) continue;
    // Prefer the React/Helmet-owned copy: it reflects the route the SPA
    // is actually rendering after a client-side navigation.
    const reactOwned = list.filter((el) => !el.hasAttribute("data-ssr"));
    const pool = reactOwned.length ? reactOwned : list;
    const survivor = pool[pool.length - 1];
    for (const el of list) {
      if (el === survivor) continue;
      el.remove();
      removed += 1;
    }
  }
  return removed;
}

export function dedupeSeoHead(head = document.head) {
  if (!head) return 0;
  const groups = new Map();
  for (const el of Array.from(head.children)) {
    const key = metaKey(el);
    if (!key) continue;
    const bucket = groups.get(key);
    if (bucket) bucket.push(el);
    else groups.set(key, [el]);
  }
  let removed = 0;
  for (const els of groups.values()) {
    if (els.length < 2) continue;
    const survivor = pickSurvivor(els);
    for (const el of els) {
      if (el === survivor) continue;
      el.remove();
      removed += 1;
    }
  }
  return removed + dedupeJsonLd(head);
}

export function installSeoHeadDedupe() {
  if (typeof document === "undefined" || !document.head) return () => {};
  if (window.__carsavrSeoDedupe) return window.__carsavrSeoDedupe;
  // Iter-419 — run SYNCHRONOUSLY inside the observer callback. The
  // previous version deferred to a microtask, which left a window where
  // a renderer snapshotting the DOM could serialise two titles.
  const observer = new MutationObserver(() => dedupeSeoHead());
  observer.observe(document.head, { childList: true });
  dedupeSeoHead();
  const dispose = () => observer.disconnect();
  window.__carsavrSeoDedupe = dispose;
  return dispose;
}
